import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Grow, Collapse } from "@mui/material";
import { useSelector } from "react-redux";
import Planner from "./pages/Planner";
import InviteCodes from "./pages/InviteCodes";
import Students from "./pages/Students";
import ExplorePage from "./pages/ExplorePage";
import Schools from "./pages/Schools";
import Teams from "./pages/Teams";
import BulkUploadJobs from "./pages/BulkUploadJobs";
import GlobalEvents from "./pages/GlobalEvents/GlobalEvents";
import PdfUploadPage from "./pages/PdfUploadPage";

import NavBar from "./shared/components/NavBar/NavBar";
import SideBar from "./shared/components/SideBar/SideBar";
import {
  startTokenVerification,
  stopTokenVerification,
} from "./shared/utils/tokenVerification";
import { getToken, getAccountType } from "./store/auth/selectors";
import { checkPermission, PERMISSION_TYPES } from "./shared/utils/permissions";

function Layout() {
  const token = useSelector(getToken);
  const accountType = useSelector(getAccountType);
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (token) {
      startTokenVerification();
    }

    return () => {
      stopTokenVerification();
    };
  }, [token]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  // Check permissions for each page
  const canViewPlanner = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_EVENTS
  );
  const canViewInviteCodes = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_INVITE_CODES
  );
  const canViewStudents = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_STUDENT_DETAILS
  );
  const canViewExplore = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_EXPLORE_STUDENT_DETAILS
  );
  const canViewSchools = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_SCHOOLS
  );
  const canViewTeams = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_TEAMS
  );
  const canViewBulkUploadJobs = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_BULK_UPLOAD_JOBS
  );
  const canViewGlobalEvents = checkPermission(
    accountType,
    PERMISSION_TYPES.VIEW_GLOBAL_STUDENT_DETAILS
  );
  const canUploadOMR = checkPermission(
    accountType,
    PERMISSION_TYPES.UPLOAD_OMR_UPLOADS
  );
  return (
    <div style={{ height: "100%" }}>
      <div className="app">
        <NavBar onToggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div className="mainContent">
          <div className={`sidebar ${isSidebarOpen ? "is-open" : ""}`}>
            <SideBar />
          </div>
          {isSidebarOpen && (
            <div className="sidebarOverlay" onClick={toggleSidebar} />
          )}
          <div className="pageContent">
            <div className="routeStack">
              {canViewPlanner && (
                <Grow in={location.pathname === "/planner"} timeout={800}>
                  <div
                    className={`routePage ${
                      location.pathname === "/planner" ? "" : "is-hidden"
                    }`}
                  >
                    <Planner />
                  </div>
                </Grow>
              )}
              {canViewInviteCodes && (
                <Grow in={location.pathname === "/invite-codes"} timeout={800}>
                  <div
                    className={`routePage ${
                      location.pathname === "/invite-codes" ? "" : "is-hidden"
                    }`}
                  >
                    <InviteCodes />
                  </div>
                </Grow>
              )}
              {canViewStudents && (
                <Grow in={location.pathname === "/students"} timeout={800}>
                  <div
                    className={`routePage ${
                      location.pathname === "/students" ? "" : "is-hidden"
                    }`}
                  >
                    <Students />
                  </div>
                </Grow>
              )}
              {canViewExplore && (
                <Grow in={location.pathname === "/explore"} timeout={800}>
                  <div
                    className={`routePage ${
                      location.pathname === "/explore" ? "" : "is-hidden"
                    }`}
                  >
                    <ExplorePage />
                  </div>
                </Grow>
              )}
              {canViewSchools && (
                <Grow in={location.pathname === "/schools"} timeout={800}>
                  <div
                    className={`routePage ${
                      location.pathname === "/schools" ? "" : "is-hidden"
                    }`}
                  >
                    <Schools />
                  </div>
                </Grow>
              )}
              {canViewTeams && (
                <Grow in={location.pathname === "/teams"} timeout={800}>
                  <div
                    className={`routePage ${
                      location.pathname === "/teams" ? "" : "is-hidden"
                    }`}
                  >
                    <Teams />
                  </div>
                </Grow>
              )}
              {canViewBulkUploadJobs && (
                <Grow
                  in={location.pathname === "/bulk-upload-jobs"}
                  timeout={800}
                >
                  <div
                    className={`routePage ${
                      location.pathname === "/bulk-upload-jobs"
                        ? ""
                        : "is-hidden"
                    }`}
                  >
                    <BulkUploadJobs />
                  </div>
                </Grow>
              )}
              {canViewGlobalEvents && (
                <div
                  className={`routePage ${
                    location.pathname.startsWith("/global-events")
                      ? ""
                      : "is-hidden"
                  }`}
                >
                  <Collapse
                    in={location.pathname.startsWith("/global-events")}
                    timeout={1200}
                    unmountOnExit
                  >
                    <div style={{ height: "100%" }}>
                      <GlobalEvents />
                    </div>
                  </Collapse>
                </div>
              )}
              {canUploadOMR && (
                <div
                  className={`routePage ${
                    location.pathname === "/pdf-upload" ? "" : "is-hidden"
                  }`}
                >
                  <Collapse
                    in={location.pathname === "/pdf-upload"}
                    timeout={1200}
                    unmountOnExit
                  >
                    <div style={{ height: "100%" }}>
                      <PdfUploadPage />
                    </div>
                  </Collapse>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Layout;
